import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { DynamicPropertyPage } from './DynamicPropertyPage';
import { API } from '../lib/api';
import { ArrowLeft, Loader2, CreditCard } from 'lucide-react';

export const ModificationPreviewPage = () => {
  const navigate = useNavigate();
  const { manageToken } = useParams();
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    const loadPreviewData = async () => {
      try {
        setLoading(true);
        
        // Get the modified form data from sessionStorage
        const sessionData = sessionStorage.getItem('modification_preview_data');
        if (!sessionData) {
          toast.error('No hay datos para previsualizar. Vuelve al formulario.');
          navigate(`/manage/${manageToken}/edit`);
          return;
        }

        const parsedSession = JSON.parse(sessionData);
        const propertyPayload = parsedSession?.property_payload;

        if (!propertyPayload) {
          toast.error('No encontramos cambios guardados para previsualizar. Vuelve a editar y prueba de nuevo.');
          navigate(`/manage/${manageToken}/edit`);
          return;
        }

        // Get the original property data
        const response = await axios.get(`${API}/modifications/manage/${manageToken}/property`);
        const originalProperty = response.data;

        // In preview mode DynamicPropertyPage uses sampleProperty.blocked_dates,
        // so fetch calendar using the ICS URLs from the form payload (or original if unchanged).
        const previewIcsUrls = propertyPayload?.booking?.ics_urls || originalProperty?.booking?.ics_urls || [];
        let blockedDates = [];
        try {
          const calendarResponse = await axios.post(`${API}/properties/calendar-from-ics`, { ics_urls: previewIcsUrls });
          blockedDates = calendarResponse.data?.blocked_dates || [];
        } catch (calendarError) {
          console.warn('Could not load calendar for modification preview:', calendarError);
        }

        // Merge preserving unchanged fields and applying only edited payload fields
        const mergedProperty = {
          ...originalProperty,
          plan: propertyPayload.plan || originalProperty.plan,
          property_data: {
            ...originalProperty.property_data,
            ...(propertyPayload.property_data || {}),
            stats: {
              ...(originalProperty.property_data?.stats || {}),
              ...(propertyPayload.property_data?.stats || {}),
            },
          },
          images: propertyPayload.images || originalProperty.images,
          location: {
            ...(originalProperty.location || {}),
            ...(propertyPayload.location || {}),
          },
          booking: {
            ...(originalProperty.booking || {}),
            ...(propertyPayload.booking || {}),
          },
          pricing: {
            ...(originalProperty.pricing || {}),
            ...(propertyPayload.pricing || {}),
          },
          contact: {
            ...(originalProperty.contact || {}),
            ...(propertyPayload.contact || {}),
          },
          services: propertyPayload.services || originalProperty.services,
          blocked_dates: blockedDates,
        };

        setPreviewData(mergedProperty);
      } catch (error) {
        console.error('Error loading preview:', error);
        toast.error('Error al cargar la previsualización');
        navigate(`/manage/${manageToken}/edit`);
      } finally {
        setLoading(false);
      }
    };

    loadPreviewData();
  }, [manageToken, navigate]);

  const handleConfirmPayment = async () => {
    try {
      setPaymentLoading(true);
      
      // Get the modified form data from sessionStorage
      const sessionData = sessionStorage.getItem('modification_preview_data');
      const parsedSession = JSON.parse(sessionData);
      const propertyPayload = parsedSession?.property_payload;

      if (!propertyPayload) {
        toast.error('No encontramos los cambios para procesar el pago. Vuelve al formulario.');
        navigate(`/manage/${manageToken}/edit`);
        return;
      }

      // Create checkout session for the modifications
      const response = await axios.post(`${API}/modifications/manage/${manageToken}/checkout/session`, {
        token: manageToken,
        origin_url: window.location.origin,
        property_payload: propertyPayload,
      });

      if (response.data.url) {
        // Clear sessionStorage before redirecting
        sessionStorage.removeItem('modification_preview_data');
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Error al iniciar el pago');
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!previewData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">No hay datos para mostrar</p>
      </div>
    );
  }

  return (
    <>
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 py-3 px-4 shadow-sm">
        <div className="mx-auto max-w-7xl flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900">Previsualización de cambios</h2>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                navigate(`/manage/${manageToken}/edit`);
              }}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a editar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmPayment}
              disabled={paymentLoading}
              className="bg-teal-600 hover:bg-teal-700 gap-2"
            >
              {paymentLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4" />
                  Confirmar y pagar 15€
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      <DynamicPropertyPage sampleProperty={previewData} />
    </>
  );
};
